import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    Suspense,
    lazy,
} from 'react';
import './App.css';
import { Stack } from '@chakra-ui/react';
import { useTheme } from 'next-themes';
import { IUpdatedQuery } from './context/queryBuilderProvider';
import { IJsonQuery } from './components/HistoryDrawer';
import { getUnixTime } from 'date-fns';

// Lazy load heavy components for better performance
const DataExplorerQuery = lazy(() => import('./components/DataExplorerQuery'));

import { useGlobal } from './context/provider';
import { useQueryBuilder } from './context/queryBuilderProvider';
import AlertComponent from './components/AlertComponent';
import { useAuth } from './context/authProvider';
import { useAuthState } from './hooks/useAuthState';
import AppHeader from './components/AppHeader';
import LoginPage from './components/LoginPage';
import './i18n';

// Alert interface
interface Alert {
    message: string;
    title: string;
    callSignOut: boolean;
}

// function PrivateRoute({ element, path }) {
//   const { isLoggedIn, login, logout } = useAuth();
//   const ele = isLoggedIn === true ? element : <Navigate to="/callback" />;
//   // return <Route path={path} element={ele} />;
// }

const App: React.FC = () => {
    // const [searchClients, setSearchClients] = useState('');

    const resetAlert: Alert = { message: '', title: '', callSignOut: false };
    const [generalAlert, setGeneralAlert] = useState<Alert>(resetAlert);
    const [showGeneralAlert, setShowGeneralAlert] = useState<boolean>(false);
    const timeExpired = useRef<boolean>(false);
    const [shouldLogOut, setShouldLogOut] = useState<boolean>(false);

    const {
        userName,
        // loggedIn,
        // signOut,
        countries,
        selectCountry,
        allClients,
        accessInfo,
    } = useGlobal();

    const {
        // userSignIn,
        updateQuery,
        selectedCountry,
        UpdateAvailableClients,
    } = useQueryBuilder();

    const { expiration } = accessInfo;

    // const location = useLocation();
    // const navigate = useNavigate();
    const { login, logout } = useAuth();
    const { isLoggedIn, userName: authUserName, checkAuthState } = useAuthState();

    // Use the auth state from the custom hook, fallback to global userName if needed
    const displayUserName = authUserName || userName;
    // const { pathname } = location;

    const signIn = (): void => {
        // console.log('Hub PrivateRoute Location', location);
        const pathname = 'dataexplorer';
        login(pathname);
        // Refresh auth state after login attempt
        setTimeout(() => checkAuthState(), 1000);
    };

    const signOut = async (): Promise<void> => {
        // console.log('Hub PrivateRoute Location sign out', location);
        try {
            await logout();
            console.log('App: Logout completed successfully');
        } catch (error) {
            console.error('App: Error during logout:', error);
        }
        // Refresh auth state after logout
        setTimeout(() => checkAuthState(), 500);
    };

    const alertMessage = useCallback(
        (message: string, title: string, callSignOut: boolean): void => {
            console.log('alertMessage called with', message);
            const qAlert: Alert = { message, title, callSignOut };
            setGeneralAlert(() => qAlert);
            setShowGeneralAlert(true);
            setShouldLogOut(callSignOut);
        },
        [],
    );

    const alertSignedOut = useCallback((): void => {
        const message = 'You are logged out - please sign in again';
        const title = 'Cannot start query';
        alertMessage(message, title, true);
    }, [alertMessage]);

    const onGeneralAlertClosed = (): void => {
        setShowGeneralAlert(false);
        setGeneralAlert(() => resetAlert);

        if (shouldLogOut) {
            console.log('App onGeneralAlertClosed', shouldLogOut);
            setShouldLogOut(false);
            signOut();
        }
    };

    // Watch for tokens expiry
    useEffect(() => {
        const now = getUnixTime(new Date());
        const expired = now > expiration.unix;
        console.log('useEffect expiration', now, expiration, expired);

        if (expired && isLoggedIn && !timeExpired.current) {
            alertMessage(
                'Your session has expired, please sign in again',
                'Session Expired',
                true,
            );
            timeExpired.current = true;
        }
    }, [expiration, isLoggedIn, alertMessage]);

    const { theme, setTheme } = useTheme();
    const toggleColorMode = () => setTheme(theme === 'dark' ? 'light' : 'dark');

    const colorModeIcon = { color: '#00a1d2' };

    const bgGradient = 'linear(135deg, #3b3e3f 0%, #5a5f61 25%, #7a8184 50%, #a2a9ad 75%, #f1f5fa 100%)';

    useEffect(() => {
        if (selectedCountry) {
            UpdateAvailableClients(
                allClients,
                selectedCountry.value,
            );
        }
    }, [selectedCountry, UpdateAvailableClients, allClients]);

    const updateFunction = (query: IUpdatedQuery | IJsonQuery): string => {
        console.log('updateFunction', query);
        // For now, we only handle IUpdatedQuery - IJsonQuery handling will be added in Phase 5
        if ('views' in query && 'user' in query) {
            updateQuery(query as IUpdatedQuery);
        } else {
            console.warn(
                'Received IJsonQuery - conversion to IUpdatedQuery not implemented yet',
            );
        }
        return 'Query updated';
    };

    return (
        // <Router>
        <Stack
            minH="100vh"
            bgGradient={bgGradient}
            color="white"
            p={0}
            minWidth="100vw"
            justify="flex-start"
        >
            {/* Show LoginPage if not authenticated, otherwise show the main app */}
            {!isLoggedIn ? (
                <LoginPage onLogin={signIn} />
            ) : (
                <>
                    <AppHeader
                        displayUserName={displayUserName}
                        userName={userName}
                        isLoggedIn={isLoggedIn}
                        signIn={signIn}
                        signOut={signOut}
                        countries={countries}
                        selectedCountry={selectedCountry}
                        selectCountry={selectCountry}
                        toggleColorMode={toggleColorMode}
                        colorModeIcon={colorModeIcon}
                        updateFunction={updateFunction}
                    />

                    <Suspense fallback={<div>Loading data explorer...</div>}>
                        <DataExplorerQuery
                            alertSignedOut={alertSignedOut}
                            alertMessage={alertMessage}
                        />
                    </Suspense>
                </>
            )}

            <AlertComponent
                isOpen={showGeneralAlert}
                onClose={onGeneralAlertClosed}
                title={generalAlert.title}
                description={generalAlert.message}
                status="error"
            />

            {/* <Routes>
        <Route exact path="/callback" render={() => <Callback />} />
        <Route
          // path="/DataExplorer"
          exact
          path="/"
          element={
            <DataExplorerQuery
              alertSignedOut={alertSignedOut}
              alertMessage={alertMessage}
            />
          }
        />

        <Route
          exact
          path="/DataExplorer"
          element={
            <DataExplorerQuery
              alertSignedOut={alertSignedOut}
              alertMessage={alertMessage}
            />
          }
        />

        <Route path="*" element={<NoMatch />} />
      </Routes> */}
        </Stack>
        // </Router>
    );
};

export default App;
